/**
 * Utility functions for drawing connection lines between assignments
 * Used for visualizing scheduled transfers between vessels
 */

/**
 * Generate a smooth bezier curve path between two points
 * Optimized for horizontal timeline layouts - curves horizontally first, then vertically
 */
export function getConnectionPath(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number
): string {
  const deltaX = targetX - sourceX;
  const deltaY = targetY - sourceY;

  // Calculate control point offset based on horizontal distance
  // Clamp to prevent overly aggressive curves
  const cpOffset = Math.min(Math.abs(deltaX) * 0.4, 100);

  // For same-row connections (horizontal only), use a simple curve
  if (Math.abs(deltaY) < 10) {
    const midX = (sourceX + targetX) / 2;
    const curveHeight = 20; // Slight arc above the line
    return `M ${sourceX} ${sourceY} Q ${midX} ${sourceY - curveHeight}, ${targetX} ${targetY}`;
  }

  // For cross-row connections, create an S-curve
  // Control points pull horizontally first, creating a smooth transition
  const cp1x = sourceX + cpOffset;
  const cp1y = sourceY;
  const cp2x = targetX - cpOffset;
  const cp2y = targetY;

  return `M ${sourceX} ${sourceY} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${targetX} ${targetY}`;
}

/**
 * Generate a step-line path (90-degree angles) for a more geometric look
 * Alternative to bezier curves
 */
export function getStepLinePath(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number
): string {
  const midX = (sourceX + targetX) / 2;
  return `M ${sourceX} ${sourceY} H ${midX} V ${targetY} H ${targetX}`;
}

/**
 * Generate an elbow path that goes down then across
 * Good for when target is below and to the right
 */
export function getElbowPath(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  cornerRadius: number = 8
): string {
  const deltaY = targetY - sourceY;
  const direction = deltaY > 0 ? 1 : -1;

  // If horizontal distance is small, just use a straight line
  if (Math.abs(targetX - sourceX) < cornerRadius * 2) {
    return `M ${sourceX} ${sourceY} L ${targetX} ${targetY}`;
  }

  // Create path with rounded corner
  const cornerY = sourceY + (Math.abs(deltaY) - cornerRadius) * direction;
  const arcSweep = deltaY > 0 ? 1 : 0;

  return `M ${sourceX} ${sourceY}
          V ${cornerY}
          A ${cornerRadius} ${cornerRadius} 0 0 ${arcSweep} ${sourceX + cornerRadius} ${targetY}
          H ${targetX}`;
}

/**
 * Calculate the midpoint of a bezier curve (approximate)
 * Useful for placing icons or labels on the connection line
 */
export function getBezierMidpoint(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number
): { x: number; y: number } {
  // For a cubic bezier, the midpoint is approximately at t=0.5
  // This is a simplified calculation that works well for our use case
  const t = 0.5;
  const deltaX = targetX - sourceX;
  const cpOffset = Math.min(Math.abs(deltaX) * 0.4, 100);

  const cp1x = sourceX + cpOffset;
  const cp1y = sourceY;
  const cp2x = targetX - cpOffset;
  const cp2y = targetY;

  // Cubic bezier formula at t=0.5
  const x =
    Math.pow(1 - t, 3) * sourceX +
    3 * Math.pow(1 - t, 2) * t * cp1x +
    3 * (1 - t) * Math.pow(t, 2) * cp2x +
    Math.pow(t, 3) * targetX;

  const y =
    Math.pow(1 - t, 3) * sourceY +
    3 * Math.pow(1 - t, 2) * t * cp1y +
    3 * (1 - t) * Math.pow(t, 2) * cp2y +
    Math.pow(t, 3) * targetY;

  return { x, y };
}
